import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { DragEvent, MouseEvent } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import BidLadderEmptyDropZone from '@/features/bidding/components/cascading/bid-ladder/drag-and-drop/BidLadderEmptyDropZone'
import BarSegment from '@/features/bidding/components/cascading/bid-ladder/shared/BarSegment'
import { getBarColors } from '@/features/bidding/components/cascading/bid-ladder/shared/getBarColors'
import type {
  BidLevelActivationGroupSymmetrical,
  DragPayloadSymmetrical,
} from '@/features/bidding/types/bidLadderInputSymmetrical'

type DraggableBidLadderStackedBarSymmetricalProps = {
  groups: BidLevelActivationGroupSymmetrical[]
  levelId: string
  /** Total MW across ALL levels (for relative bar width comparison) */
  totalMWAcrossAll: number
  /** Groups to show as preview (when dragging over this level) */
  previewGroups?: BidLevelActivationGroupSymmetrical[]
  /** Currently hovered regulation object code (for cross-highlighting) */
  hoveredRegulationObjectCode?: string | null
  onContextMenu: (event: MouseEvent, group: BidLevelActivationGroupSymmetrical) => void
  onDragStartFromBar: (payload: DragPayloadSymmetrical) => void
  onDragEndFromBar: () => void
  onGroupHover?: (regulationObjectCode: string | null) => void
  /** Callback for keyboard-based move (arrow keys) */
  onKeyboardMove?: (group: BidLevelActivationGroupSymmetrical, moveDirection: 'up' | 'down') => void
  /** Key of group that should receive focus (format: levelId-regulationObjectCode) */
  focusedGroupKey?: string | null
  /** Callback to clear focused group key after focus is set */
  onClearFocusedGroupKey?: () => void
}

function DraggableBidLadderStackedBarSymmetrical({
  groups,
  levelId,
  totalMWAcrossAll,
  previewGroups,
  hoveredRegulationObjectCode,
  onContextMenu,
  onDragStartFromBar,
  onDragEndFromBar,
  onGroupHover,
  onKeyboardMove,
  focusedGroupKey,
  onClearFocusedGroupKey,
}: Readonly<DraggableBidLadderStackedBarSymmetricalProps>) {
  const theme = useTheme()
  const colors = getBarColors(theme)
  const { barColorMedium } = colors

  // Refs for focus management
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Focus the group when focusedGroupKey matches
  useEffect(() => {
    if (!focusedGroupKey) return

    const ref = groupRefs.current.get(focusedGroupKey)
    if (ref) {
      ref.focus()
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
      onClearFocusedGroupKey?.()
    }
  }, [focusedGroupKey, onClearFocusedGroupKey])

  const previewGroupsList = previewGroups ?? []

  // Calculate MW totals
  const currentMW = groups.reduce((sum, g) => sum + g.capacityMW, 0)
  const previewMW = previewGroupsList.reduce((sum, g) => sum + g.capacityMW, 0)
  const levelTotalMW = currentMW + previewMW

  // Calculate width percentage
  const adjustedTotal = totalMWAcrossAll + previewMW
  const levelWidthPercent = adjustedTotal > 0 ? (levelTotalMW / adjustedTotal) * 100 : 0

  const handleDragStart = useCallback(
    (event: DragEvent, group: BidLevelActivationGroupSymmetrical) => {
      const relatedGroups = groups.filter((g) => g.regulationObjectCode === group.regulationObjectCode)

      const payload: DragPayloadSymmetrical = {
        regulationObjectCodes: [group.regulationObjectCode],
        activationGroups: [],
        sourceLevelId: levelId,
        previewGroups: relatedGroups,
      }
      event.dataTransfer.setData('application/json', JSON.stringify(payload))
      event.dataTransfer.effectAllowed = 'move'
      onDragStartFromBar(payload)
    },
    [levelId, groups, onDragStartFromBar],
  )

  const handleDragEnd = useCallback(() => {
    onDragEndFromBar()
  }, [onDragEndFromBar])

  // Empty state - show drop zone
  if (groups.length === 0 && previewGroupsList.length === 0) {
    return <BidLadderEmptyDropZone colors={colors} />
  }

  // Preview-only state
  if (groups.length === 0 && previewGroupsList.length > 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          height: 32,
          flex: 1,
          gap: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: `${levelWidthPercent}%`,
            height: '100%',
            gap: '2px',
          }}
        >
          {previewGroupsList.map((group) => (
            <Box
              key={group.uuid}
              sx={{
                flex: 1,
                height: '100%',
                backgroundColor: barColorMedium,
                borderRadius: '3px',
                animation: 'scaleIn 0.15s ease-out',
                '@keyframes scaleIn': {
                  '0%': { transform: 'scaleX(0)', opacity: 0 },
                  '100%': { transform: 'scaleX(1)', opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: 32,
        flex: 1,
        gap: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: `${levelWidthPercent}%`,
          height: '100%',
          gap: '2px',
        }}
      >
        {groups.map((group) => {
          const segmentWidthPercent = levelTotalMW > 0 ? (group.capacityMW / levelTotalMW) * 100 : 0
          const isHighlighted = hoveredRegulationObjectCode === group.regulationObjectCode
          const groupKey = `${levelId}-${group.regulationObjectCode}`

          return (
            <BarSegment
              key={group.uuid}
              draggable
              capacityMW={group.capacityMW}
              code={group.code}
              colors={colors}
              groupKey={groupKey}
              isHighlighted={isHighlighted}
              refCallback={(el: HTMLDivElement | null) => {
                if (el) {
                  groupRefs.current.set(groupKey, el)
                } else {
                  groupRefs.current.delete(groupKey)
                }
              }}
              regulationObjectCode={group.regulationObjectCode}
              segmentWidthPercent={segmentWidthPercent}
              uuid={group.uuid}
              onContextMenu={(e) => onContextMenu(e, group)}
              onDragEnd={handleDragEnd}
              onDragStart={(e) => handleDragStart(e, group)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                  e.preventDefault()
                  const rect = e.currentTarget.getBoundingClientRect()
                  const syntheticEvent = {
                    preventDefault: () => {},
                    clientX: rect.left + rect.width / 2,
                    clientY: rect.top + rect.height / 2,
                  } as MouseEvent
                  onContextMenu(syntheticEvent, group)
                }
                if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && onKeyboardMove) {
                  e.preventDefault()
                  onKeyboardMove(group, e.key === 'ArrowUp' ? 'up' : 'down')
                }
              }}
              onMouseEnter={() => onGroupHover?.(group.regulationObjectCode)}
              onMouseLeave={() => onGroupHover?.(null)}
            />
          )
        })}
        {previewGroupsList.map((group) => {
          const segmentWidthPercent = levelTotalMW > 0 ? (group.capacityMW / levelTotalMW) * 100 : 0
          return (
            <Box
              key={`preview-${group.uuid}`}
              sx={{
                width: `${segmentWidthPercent}%`,
                minWidth: 12,
                height: '100%',
                backgroundColor: barColorMedium,
                borderRadius: '3px',
                animation: 'scaleIn 0.15s ease-out',
                '@keyframes scaleIn': {
                  '0%': { transform: 'scaleX(0)', opacity: 0 },
                  '100%': { transform: 'scaleX(1)', opacity: 1 },
                },
              }}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default DraggableBidLadderStackedBarSymmetrical
